<template>
  <GlossaryTopbar
    :isEditMode="true"
    :is-disabled="isDisabled"
    @back="goBack"
    @save="save"
    @download-csv="downloadCsv"
    @upload-csv="uploadCsv"
    @remove-glossary="goBack"
    :glossary="glossary"
    :currentRows="rows"
    :currentHeaders="headers"
  />
  <div class="glossaries-container">
    <div class="table-container">
      <table @paste.prevent="handlePaste">
        <thead>
          <tr>
            <th v-for="(header, index) in headers" :key="index">
              <div class="column-header-container">
                <BbInput class="row-input" size="small" :value="header.name" readonly />
                <BbBtn
                  v-if="header.isCustom"
                  class="edit-button"
                  size="large"
                  type="icon"
                  icon="edit"
                  icon-size="small"
                  icon-type="outlined"
                  @click="openEditCustomLanguagePopup(index)"
                ></BbBtn>
                <BbBtn
                  class="delete-button"
                  size="large"
                  type="icon"
                  icon="delete"
                  icon-size="small"
                  icon-type="outlined"
                  @click="openDeleteLangConfirmPopup(index)"
                ></BbBtn>
              </div>
            </th>
            <th>
              <div class="last-column-container">
                <TextBody v-if="!headers.length" i18n-key="glossaries.getStartedPickA" tag="span" :body-type="2" />
                <SupportedLanguagesMenu
                  @change-language="addLanguageColumn"
                  @create-custom="customLanguagePopupOpened = true"
                  :supported-languages="filteredSupportedLanguages"
                />
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, rowIndex) in rows" :key="rowIndex">
            <td
              v-for="(item, colIndex) in row"
              :key="colIndex"
              :class="{
                'active-cell': selectedCell.row === rowIndex && selectedCell.column === colIndex,
              }"
            >
              <BbInput
                class="row-input"
                v-model="rows[rowIndex][colIndex]"
                :ref="setInputRef(rowIndex, colIndex)"
                size="small"
                @focus="handleSelectedCell(rowIndex, colIndex)"
                @blur="clearActiveCell"
              />
            </td>
            <td></td>
            <!-- Empty cell for the dropdown column -->
          </tr>
        </tbody>
      </table>
      <BbBtn
        class="new-row"
        size="small"
        type="primary"
        color="#6222e3"
        :is-disabled="!headers.length"
        @click="handleNewRow"
      >
        <template #prepend>
          <BbIcon size="small" name="plusincircle" />
        </template>
        <TextBody i18n-key="glossaries.newRow" />
      </BbBtn>
    </div>
  </div>
  <BbToastMessage :modelValue="showCsvToast" :title="csvToastTitle" location="top right" />
  <CustomLanguagePopup
    :popup-opened="customLanguagePopupOpened"
    :supported-languages="supportedLanguages"
    :existing-names="existingColumnNames"
    :editing="editingCustomLanguage"
    @update:open="onCustomLanguagePopupOpenChange"
    @execute:action="saveCustomLanguage"
  />
  <WarningPopup
    :popup-opened="confirmDeletePopupOpened"
    title-key="glossaries.deleteLanguage"
    warning-message-key="glossaries.deleteLanguageWarning"
    confirm-text-key="modals.confirmDeleteText"
    @update:open="confirmDeletePopupOpened = false"
    @execute:action="removeLanguageColumn"
  />
</template>

<script>
import { useI18n } from 'vue-i18n';
import { computed, nextTick, onMounted, reactive, ref } from 'vue';
import { applyCSVToRows, canBuildGlossary, downloadAsCSV, mapEntityEntriesToTableEntries } from '~/utils/glossary';
import useGlossaries from '~/composables/useGlossaries';
import GlossaryTopbar from '~/components/glossary/GlossaryTopbar';
import CustomLanguagePopup from '~/components/glossary/CustomLanguagePopup';
import SupportedLanguagesMenu from '~/components/glossary/SupportedLanguagesMenu';
import TextBody from '~/components/common/TextBody';
import WarningPopup from '~/components/common/WarningPopup';

export default {
  components: { CustomLanguagePopup, GlossaryTopbar, TextBody, SupportedLanguagesMenu, WarningPopup },
  emits: ['navigate-back', 'save'],
  props: {
    glossary: {
      type: Object,
      default: () => {},
    },
  },
  setup(props, { emit }) {
    const { t } = useI18n();
    const { getSupportedLanguages } = useGlossaries();
    const showCsvToast = ref(false);
    const csvToastTitle = ref('');
    const notify = (title) => {
      csvToastTitle.value = title;
      showCsvToast.value = true;
      setTimeout(() => {
        showCsvToast.value = false;
      }, 4000);
    };

    const selectedCell = reactive({ row: 0, column: 0 });

    const confirmDeletePopupOpened = ref(false);
    const isLanguageMenuOpened = ref(false);
    const customLanguagePopupOpened = ref(false);
    // index of the custom column being edited, null when the popup creates a new one
    const editingColumnIndex = ref(null);
    const inputsRefs = ref([]);
    const indexColToBeRemoved = ref(0);
    const headers = ref([]);
    const rows = ref([]);
    const supportedLanguages = ref([]);

    const isDisabled = computed(() => !canBuildGlossary(headers.value));

    const filteredSupportedLanguages = computed(() =>
      supportedLanguages.value.filter(
        ({ language }) => !headers.value.some((header) => !header.isCustom && header.language === language),
      ),
    );

    const setInputRef = (rowIndex, colIndex) => {
      return (el) => {
        if (!inputsRefs.value[rowIndex]) {
          inputsRefs.value[rowIndex] = [];
        }
        inputsRefs.value[rowIndex][colIndex] = el;
      };
    };

    const focusOnNewColumn = () => {
      const newColIndex = headers.value.length - 1;
      const firstRowInput = inputsRefs.value[0][newColIndex];
      if (firstRowInput) {
        firstRowInput.focus();
      }
    };

    const focusOnNewRow = () => {
      const newRowIndex = rows.value.length - 1;
      const firstRowInput = inputsRefs.value[newRowIndex][0];
      if (firstRowInput) {
        firstRowInput.focus();
      }
    };

    const removeLanguageColumn = () => {
      // remove language from header
      // Check if the index exists in the sub-array
      if (headers.value.length > indexColToBeRemoved.value) {
        headers.value.splice(indexColToBeRemoved.value, 1);
      }

      // remove words for that language
      rows.value.forEach((row) => {
        // special case when last column was removed
        if (!headers.value.length) {
          rows.value = [];
        } else {
          // Check if the index exists in the sub-array
          row.splice(indexColToBeRemoved.value, 1);
        }
      });
      indexColToBeRemoved.value = 0;
      confirmDeletePopupOpened.value = false;
    };

    const addLanguageColumn = (newLanguage) => {
      if (newLanguage) {
        headers.value.push(newLanguage);
        // when empty state, should be treated different
        if (!rows.value.length) {
          rows.value.push(['']);
        } else {
          rows.value.forEach((row) => {
            row.push(''); // Add empty translation for new language
          });
        }
      }

      // Focus the first cell in the new column after the DOM updates
      nextTick(() => {
        focusOnNewColumn();
      });
    };

    const editingCustomLanguage = computed(() => headers.value[editingColumnIndex.value] ?? null);

    // the column being edited can keep its own name
    const existingColumnNames = computed(() =>
      headers.value.filter((_, index) => index !== editingColumnIndex.value).map(({ name }) => name),
    );

    const openEditCustomLanguagePopup = (index) => {
      editingColumnIndex.value = index;
      customLanguagePopupOpened.value = true;
    };

    const onCustomLanguagePopupOpenChange = (isOpened) => {
      customLanguagePopupOpened.value = isOpened;
      if (!isOpened) editingColumnIndex.value = null;
    };

    const saveCustomLanguage = ({ name, parentLanguage }) => {
      const parent = supportedLanguages.value.find(({ language }) => language === parentLanguage);
      if (editingColumnIndex.value === null) {
        // language stays the parent's code so providers keep receiving it, name is what the user sees
        addLanguageColumn({ name, language: parent.language, isCustom: true });
      } else {
        // only this glossary's column changes: the phrases stay, translations already made are not touched
        headers.value[editingColumnIndex.value] = {
          ...headers.value[editingColumnIndex.value],
          name,
          language: parent.language,
        };
      }
      onCustomLanguagePopupOpenChange(false);
    };

    const removeRow = (index) => {
      rows.value.splice(index, 1);
    };

    const handlePaste = (event) => {
      const paste = (event.clipboardData || window.clipboardData).getData('text');
      const rows = paste.split('\n').map((row) => row.split('\t'));
      applyPasteData(rows);
      event.preventDefault();
    };

    const applyPasteData = (pastedRows) => {
      const startRow = selectedCell.row;
      const startCol = selectedCell.column;

      pastedRows.forEach((row, i) => {
        const rowIndex = startRow + i;
        // Check if the rowIndex exceeds current table rows
        if (rowIndex >= rows.value.length) {
          // If so, create a new empty row with the same number of columns
          let newRow = new Array(headers.value.length).fill('');
          rows.value.push(newRow);
        }
        row.forEach((cell, j) => {
          const colIndex = startCol + j;
          if (colIndex < rows.value[rowIndex].length) {
            rows.value[rowIndex][colIndex] = cell;
          } else {
            // Optionally handle cases where pasted data exceeds current column count
            // This could include adding new columns or ignoring excess data
          }
        });
      });

      nextTick(() => {
        focusOnNewRow();
      });
    };

    const handleSelectedCell = (rowIndex, colIndex) => {
      selectedCell.row = rowIndex;
      selectedCell.column = colIndex;
    };

    const clearActiveCell = () => {
      selectedCell.row = null;
      selectedCell.column = null;
    };

    const handleNewRow = () => {
      let newRow = new Array(headers.value.length).fill('');
      rows.value.push(newRow);
      // Focus the first cell in the new column after the DOM updates
      nextTick(() => {
        focusOnNewRow();
      });
    };

    const save = (publicValue, newName) => {
      emit('save', headers.value, rows.value, props.glossary, publicValue, newName);
    };

    const goBack = () => emit('navigate-back');

    const openDeleteLangConfirmPopup = (index) => {
      confirmDeletePopupOpened.value = true;
      indexColToBeRemoved.value = index;
    };

    const downloadCsv = () => {
      downloadAsCSV(headers.value, rows.value, props.glossary.title);
    };

    const uploadCsv = async (file) => {
      if (!file.name.toLowerCase().endsWith('.csv')) {
        notify(t('glossaries.csvInvalidFile'));
        return;
      }
      const newRows = applyCSVToRows(await file.text(), headers.value, rows.value);
      if (!newRows) {
        notify(t('glossaries.csvNoMatchingLanguages'));
        return;
      }
      rows.value = newRows;
    };

    onMounted(async () => {
      const entries = props.glossary?.entries || [];
      const { headers: headersValue, rows: rowsValue } = mapEntityEntriesToTableEntries(entries);
      headers.value = headersValue;
      rows.value = rowsValue;
      supportedLanguages.value = await getSupportedLanguages();
    });

    return {
      addLanguageColumn,
      editingCustomLanguage,
      existingColumnNames,
      onCustomLanguagePopupOpenChange,
      openEditCustomLanguagePopup,
      saveCustomLanguage,
      customLanguagePopupOpened,
      clearActiveCell,
      confirmDeletePopupOpened,
      downloadCsv,
      uploadCsv,
      showCsvToast,
      csvToastTitle,
      filteredSupportedLanguages,
      goBack,
      isDisabled,
      isLanguageMenuOpened,
      headers,
      handleNewRow,
      handlePaste,
      handleSelectedCell,
      openDeleteLangConfirmPopup,
      removeLanguageColumn,
      removeRow,
      rows,
      save,
      selectedCell,
      setInputRef,
      supportedLanguages,
    };
  },
};
</script>

<style scoped lang="scss">
.glossaries-container {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  overflow: auto;
  background-color: $bb-neutral-0;
  padding: $bb-spacing-4;

  .table-container {
    width: 100%;
    max-height: 95%; /* Adjust based on your needs */
    overflow: auto;

    .new-row {
      margin-top: $bb-spacing-1;
    }

    thead th {
      position: sticky;
      top: 0;
      z-index: 20; /* Higher than the tbody content */
    }

    table {
      width: 100%;
      // border-collapse: collapse;
    }

    td.active-cell {
      background-color: $bb-interface-4;
      border: 1px solid $bb-interface-7;
    }

    tr .delete-row-button {
      border: none;
      align-items: center;
    }

    th:hover .column-header-container .delete-button,
    th:hover .column-header-container .edit-button {
      display: inherit; /* Show buttons on hover */
    }

    th {
      border: 1px solid $bb-neutral-4;
      padding: $bb-spacing-2;
      text-align: left;
      background-color: $bb-neutral-5;

      .column-header-container {
        display: flex;
        align-items: center;

        .edit-button {
          display: none;
          cursor: pointer;
          color: $bb-neutral-1 !important;
          background-color: $bb-neutral-0 !important;
          border: none !important;
          box-shadow: none !important;

          :deep(.v-btn__content) {
            color: $bb-neutral-1;
          }
        }

        .delete-button {
          display: none;
          cursor: pointer;
          color: $bb-interface-5 !important;
          background-color: $bb-interface-5 !important;
          border: 1px solid $bb-interface-5;

          :deep(.v-btn__content) {
            color: white;
          }
        }
      }
    }

    th:last-child {
      /* Your styles for the second th element from the end */
      :deep(span) {
        font-weight: 500 !important;
      }

      .last-column-container {
        display: flex;
        gap: $bb-spacing-2;
        align-items: center;
      }
    }

    td:last-child {
      border-top: 1px solid transparent;
    }

    th:last-child {
      border-left: 1px solid transparent;
    }

    td {
      border: 1px solid $bb-neutral-4;
      padding: $bb-spacing-2;
      text-align: left;
      background-color: $bb-neutral-0;
      min-width: 100px;
    }

    .row-input {
      @include bb-regular-text-styles('lg');
      width: 100%;
      box-sizing: border-box; /* Ensures the input fits well within the cell */

      :deep(.v-input .v-input__control .v-field) {
        .v-field__outline {
          .v-field__outline__start {
            border: none;
          }

          .v-field__outline__end {
            border: none;
          }
        }
      }
    }
  }
}
</style>
